import type { PartialDeep } from 'type-fest'

export const useBusinessStore = defineStore('business', {
  state: (): BusinessData => ({
    id: '',
    admins: [],
    members: [],
    domains: [],
    email: '',
    name: '',
    phone: '',
    createdAt: '',
    gtcAcceptedAt: '',
    dpaAcceptedAt: '',
    billing: {
      address1: '',
      address2: '',
      city: '',
      country: '',
      postalCode: '',
      vat: '',
      currency: undefined,
      paymentMethod: undefined,
      paymentStatus: undefined,
      stripeSubscriptionStatus: undefined,
    },
    pricePlan: {
      licenses: {
        idents: {
          eidas: {
            payForMembers: false,
            payForInvitations: false,
            identMethod: undefined,
          },
          zertes: {
            payForMembers: false,
            payForInvitations: false,
            identMethod: undefined,
          },
        },
      },
      current: {
        base: null,
      },
      next: {
        base: null,
      },
      discounts: [],
    },
    settings: {
      supportContact: { email: '', phone: '' },
      signatureRequests: { part11: { enabled: false } },
      companyBranding: {
        logo: { height: '', width: '', url: '' },
        miniLogo: { height: '', width: '', url: '' },
      },
      mailService: {
        attachments: {
          signedDocument: { enabled: false, customisable: false },
          signatureProtocol: { enabled: false, customisable: false },
        },
      },
      groups: {
        maxGroups: 0,
        maxParticipants: 0,
        maxObserverGroupsPerSr: 0,
      },
      identShop: { url: '' },
    },
    seals: [],
    stripe: {
      subscriptionStatus: '',
    },
    aes4biz: {
      enabled: false,
      representativeName: '',
      representativeEmail: '',
      commercialRegister: '',
      domains: [],
    },
    dashboard: null,
    emailDomain: null,
    defaultSender: '',
  }),
  getters: {
    branding: state => {
      if ('companyBranding' in state.settings) {
        if (state.settings.companyBranding?.logo !== undefined) {
          return state.settings.companyBranding
        } else {
          return Object.assign({}, state.settings.companyBranding, {
            logo: { url: '' },
            miniLogo: { height: '', width: '', url: '' },
          })
        }
      }
      return {
        logo: {
          url: '',
        },
        miniLogo: {
          url: '',
        },
      }
    },
    currentPricePlan(state): ActivePricePlan | null {
      return state.pricePlan.current.base
    },
    isGtcAccepted(state): boolean {
      return Boolean(state.gtcAcceptedAt)
    },
    previousPricePlans(state): { base: ActivePricePlan }[] {
      return state.pricePlan.previous || []
    },
    /**
     * Infers the plan name from the plan id.
     *
     * We don't get a human-readable name from the backend, so we need to compute it.
     */
    currentPricePlanName(): PricePlanName | '' {
      const id = this.currentPricePlan?.type
      const inferredName = id?.split('_').shift()
      if (!inferredName) return ''
      return (inferredName.charAt(0) + inferredName.slice(1).toLowerCase()) as PricePlanName
    },
    currentPaymentStatus(): PaymentStatus | null {
      return this.billing.paymentStatus || null
    },
    isStarterPlan(): boolean {
      return isStarterPricePlan(this.currentPricePlan?.type)
    },
    hasPreviouslyUsedTrial(): boolean {
      return this.previousPricePlans.some(plan => plan.base.trial || plan.base.type.startsWith('TRIAL_V1'))
    },
    isCancelling(): boolean {
      return this.pricePlan?.next?.base?.type === 'CANCELED'
    },
    isCancelled(): boolean {
      return this.currentPricePlan?.type === 'CANCELED'
    },
    isTrialing(state): boolean {
      return state.billing.paymentMethod === 'TRIALING' || Boolean(state.pricePlan.current.base?.trial)
    },
    isChargebeeTrial(state): boolean {
      return Boolean(state.pricePlan.current.base?.trial)
    },
    isStarterOrCbTrial(): boolean {
      /**
       * For the Starter and CB Trial, we update an ongoing subscription in CB, this forces us
       * to keep the same currency.
       */
      return this.isChargebeeTrial || this.isStarterPlan
    },
    isTrialExpired(state): boolean {
      return state.billing.paymentMethod === 'NEEDS_PAYMENT_METHOD'
    },
    isLocked(state): boolean {
      return state.billing.paymentMethod === 'BLOCKED'
    },
    isPaymentUpdateRequired(state): boolean {
      return state.billing.paymentMethod === 'BLOCKED' && state.billing.paymentStatus === 'UPDATE_REQUIRED'
    },
    isManuallyInvoiced(state): boolean {
      return state.billing.paymentMethod === 'MANUAL'
    },
    isChargebeeBusiness(state): boolean {
      return state.billing.paymentMethod === 'CHARGEBEE'
    },
    trialEndDate(state): Date | null {
      if (!this.isTrialing) return null
      const trialEndDateString = state.pricePlan.current.base?.endAt
      return trialEndDateString ? new Date(trialEndDateString) : null
    },
    hasPaymentIssues(state): boolean {
      return (
        (!this.isLocked &&
          state.stripe.subscriptionStatus &&
          !['active', 'incomplete_expired'].includes(state.stripe.subscriptionStatus)) ||
        Boolean(
          state.stripe?.subscriptionStatus &&
          !['active', 'incomplete_expired'].includes(state.stripe?.subscriptionStatus)
        )
      )
    },
    companyHasAutoDeletionEnabled(state) {
      return state.settings?.signatureRequests?.retention?.enabled ?? false
    },
    companyHasLegacyAutoDeletion(state) {
      return state.settings?.signatureRequests?.retention?.terminalState ?? false
    },
    companyHasPart11(state) {
      return state.settings?.signatureRequests?.part11?.enabled ?? false
    },
    hasDiscountApplied(state) {
      return Boolean(state.pricePlan.discounts?.length)
    },
    discountPromoCode(state) {
      if (!this.hasDiscountApplied) return null
      return state.pricePlan.discounts?.[0]?.promoCode
    },
    discountType(state) {
      if (!this.hasDiscountApplied) return null
      return state.pricePlan.discounts?.[0]?.type || ''
    },
    discountExpirationDate(state) {
      if (!this.hasDiscountApplied) return null
      const expiresAt = state.pricePlan.discounts?.[0]?.expiresAt
      if (!expiresAt) return null
      return new Date(expiresAt)
    },
    isDiscountActive(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0]?.status === 'ACTIVE'
    },
    isDiscountUsedUp(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0]?.status === 'USED_UP'
    },
    isSMGDiscountApplied(state) {
      if (!this.hasDiscountApplied) return false
      return state.pricePlan.discounts?.[0]?.type?.toLocaleLowerCase().startsWith('smg_')
    },
    isIdentificationActive(state) {
      return (
        (state.pricePlan.licenses?.idents?.eidas.payForInvitations ||
          state.pricePlan.licenses?.idents?.eidas.payForMembers ||
          state.pricePlan.licenses?.idents?.zertes.payForInvitations ||
          state.pricePlan.licenses?.idents?.zertes.payForMembers) ??
        false
      )
    },
    isZertesActiveForMembers(state) {
      return state.pricePlan.licenses?.idents?.zertes.payForMembers ?? false
    },
    isEidasActiveForMembers(state) {
      return state.pricePlan.licenses?.idents?.eidas.payForMembers ?? false
    },
    isIdentMethodNect(state) {
      return state.pricePlan.licenses?.idents?.eidas.identMethod === 'nect'
    },
    isCelloEnabled(state) {
      return Boolean(state.settings.affiliate?.enabled)
    },
    isScimEnabled(state) {
      return Boolean(state.settings.scim?.enabled)
    },
    isDirectSignEnabled(state) {
      return state.settings?.mabSettings?.directSign?.enabled ?? false
    },
    groupsLimit(state) {
      return state.settings.groups
    },
    observerGroupsLimit(state) {
      return state.settings.groups.maxObserverGroupsPerSr
    },
    membersCount(state): number {
      return state.members.length
    },
  },
  actions: {
    async registerBusiness(registerData: RegisterBusinessPayload) {
      const { businessRepository } = useLegacyApi()
      const { track } = useAnalytics()

      const businessData = await businessRepository.create(registerData)

      if (registerData.attributes?.companySize || registerData.attributes?.skribbleSigningVolume) {
        track(ANALYTICS_EVENTS.BUSINESS_REGISTERED, {
          businessId: businessData.id,
          companySize: registerData.attributes?.companySize,
          signingVolume: registerData.attributes?.skribbleSigningVolume,
        })
      }
      await useAuthStore().refreshPermissions()

      this.$patch(businessData)
    },
    async ensureBusiness(strategy: EnsureStrategy = 'cache-first') {
      const userStore = useUserStore()
      return this.ensureOnce('business', () => userStore.loadUserBusinessData(), strategy)
    },
    async ensureSettings(strategy: EnsureStrategy = 'cache-first') {
      // Settings can only be fetched once we know which business we are
      if (!this.id) return

      return this.ensureOnce(
        'settings',
        async () => {
          const { $api } = useNuxtApp()
          const settings = await $api(`/api/businesses/${this.id}/settings`)
          this.parseAndPatchSettings(settings)
        },
        strategy
      )
    },
    /**
     * Prefetches the business settings without waiting for the result.
     *
     * Errors are logged but not thrown, so the calling component is not affected.
     */
    prefetchSettings() {
      this.ensureSettings().catch(error => {
        // Resolved here rather than in the action, so no error hook is registered on the calling component
        const { logError } = useErrorHandling()
        logError('Failed to load business settings', error)
      })
    },
    async getBusiness() {
      if (!this.id) return

      const { $api } = useNuxtApp()
      const businessData = await $api(`/api/businesses/${this.id}`)
      // test mocks sometimes send null response which turns to '', so this guard is needed to prevent undefined erros
      if (!businessData) return

      // override groups property because v1 is returning 0 values always
      businessData.settings = {
        ...businessData.settings,
        groups: this.groupsLimit,
      }

      this.$patch(businessData)
    },
    async acceptGtc() {
      const { businessRepository } = useLegacyApi()
      const updatedData = await businessRepository.acceptGtc(this.id)
      this.$patch(updatedData)
    },
    async getBillingAddress() {
      const { businessRepository } = useLegacyApi()
      const billing = await businessRepository.getBillingAddress(this.id)

      this.$patch({ billing })
    },
    async convertFreeToStarter(gtc: boolean, currency: string) {
      const { businessRepository } = useLegacyApi()
      const businessData = await businessRepository.convertFreeToStarter({ gtc, currency })
      await useAuthStore().refreshPermissions()

      this.$patch(businessData)
    },
    async updateContactInfo(contactInfo: Partial<BusinessContactInfo>) {
      const { $api } = useNuxtApp()
      const updatedData = await $api(`/api/businesses/${this.id}/base`, {
        method: 'PUT',
        body: contactInfo,
      })
      this.$patch(updatedData)
    },
    async updateSupportContact(contactData: SupportContactData) {
      const { $api } = useNuxtApp()

      const updatedSupportContact = await $api(`/api/businesses/${this.id}/settings/support-contact`, {
        method: 'PUT',
        body: contactData,
      })

      this.parseAndPatchSettings({ ...this.settings, supportContact: updatedSupportContact })
    },
    async updateBillingAddress(addressData: Partial<Address>) {
      const { $api } = useNuxtApp()
      const updatedData = await $api(`/api/businesses/${this.id}/billing`, { method: 'PUT', body: addressData })
      this.$patch(updatedData)
    },
    async deleteBusiness() {
      const { $api } = useNuxtApp()
      await $api(`/api/businesses/${this.id}`, { method: 'DELETE' })
    },
    async revokeCancelBusiness() {
      const { $api } = useNuxtApp()
      await $api(`/api/businesses/${this.id}/revoke-delete`, { method: 'POST' })
      await this.getBusiness()

      if (this.pricePlan?.next?.base?.type === 'CANCELED') {
        this.pricePlan.next.base = null
      }
    },
    async listApiKeys(): Promise<BusinessApiKeyList> {
      const { $api } = useNuxtApp()

      const response = await $api(`/api/businesses/${this.id}/api-keys`)
      return response.items
    },
    async createApiKey(data: {
      type: APIKeyType
      description: string
      role?: APIKeyRole
    }): Promise<BusinessApiKeyCreationResponse> {
      const { $api } = useNuxtApp()

      return await $api(`/api/businesses/${this.id}/api-keys`, {
        method: 'POST',
        body: data,
      })
    },
    async updateApiKey(name: string, data: { description?: string }): Promise<BusinessApiKeyUpdateResponse> {
      const { $api } = useNuxtApp()

      return await $api(`/api/businesses/${this.id}/api-keys/${name}`, {
        method: 'PATCH',
        body: data,
      })
    },
    async deleteApiKey(name: string): Promise<void> {
      const { $api } = useNuxtApp()

      await $api(`/api/businesses/${this.id}/api-keys/${name}`, {
        method: 'DELETE',
      })
    },
    async resetApiKey(name: string): Promise<BusinessApiKeyResetResponse> {
      const { $api } = useNuxtApp()

      return await $api(`/api/businesses/${this.id}/api-keys/${name}/reset`, {
        method: 'POST',
      })
    },
    async refreshSeals(): Promise<void> {
      if (!this.id) return

      const { $api } = useNuxtApp()

      const response = await $api(`/api/businesses/${this.id}/seals`)
      const seals = response.items

      this.$patch({ seals })
    },
    async getSealImage(name: string): Promise<Blob> {
      const { $api } = useNuxtApp()
      return await $api(`/api/businesses/${this.id}/seals/${name}/image`)
    },
    async addDomain(domain: string) {
      const { businessRepository } = useLegacyApi()
      const domains = await businessRepository.addDomain(this.id, domain)
      this.$patch({ domains })
    },
    async removeDomain(domain: string) {
      const { businessRepository } = useLegacyApi()
      const domains = await businessRepository.removeDomain(this.id, domain)
      this.$patch({ domains })
    },
    async updateSettings(settings: PartialDeep<BusinessPreferences>) {
      if (!this.id) return
      const { $api } = useNuxtApp()

      const updatedSettings = await $api(`/api/businesses/${this.id}/settings`, {
        method: 'PATCH',
        body: settings,
      })

      this.parseAndPatchSettings(updatedSettings)
    },
    async cancelTrial() {
      const { businessRepository } = useLegacyApi()
      return businessRepository.cancelTrial(this.id)
    },
    parseAndPatchSettings(settings: BusinessPreferences) {
      if (settings.supportContact) {
        /* remove possibly bad email, as this comes from user defined value */
        if (settings.supportContact.email && !isEmail(settings.supportContact.email)) {
          settings.supportContact.email = ''
        }
        /* remove possibly bad phone, as this comes from user defined value */
        if (settings.supportContact.phone && !isPhoneNumber(settings.supportContact.phone)) {
          settings.supportContact.phone = ''
        }
      }
      this.$patch({ settings })
    },
    async getEmailDomain() {
      if (!this.id) return
      const { businessRepository } = useLegacyApi()
      const emailDomain = await businessRepository.getEmailDomain(this.id)
      this.$patch({ emailDomain })
    },
    async getDefaultSender() {
      if (!this.id) return
      const { businessRepository } = useLegacyApi()
      const defaultSender = await businessRepository.getDefaultSender(this.id)
      this.$patch({ defaultSender })
    },
    async registerEmailDomain(payload: RegisterEmailDomainPayload) {
      const { businessRepository } = useLegacyApi()
      const emailDomain = await businessRepository.registerEmailDomain(this.id, payload)
      this.$patch({ emailDomain })
    },
    async updateEmailDomain(updates: UpdateEmailDomainPayload) {
      const { businessRepository } = useLegacyApi()
      const emailDomain = await businessRepository.updateEmailDomain(this.id, updates)
      this.$patch({ emailDomain })
    },
    async verifyEmailDomain() {
      const { businessRepository } = useLegacyApi()
      const emailDomain = await businessRepository.verifyEmailDomain(this.id)
      this.$patch({ emailDomain })
    },
    async removeEmailDomain() {
      const { businessRepository } = useLegacyApi()
      await businessRepository.removeEmailDomain(this.id)
      this.$patch({ emailDomain: null })
    },
    async loadDashboard() {
      const { $api } = useNuxtApp()

      const dashboardData = await $api(`/api/businesses/${this.id}/statistics`)
      this.$patch({ dashboard: dashboardData })
    },
    clearBusiness() {
      this.$clearEnsured()
      this.$reset()
    },
    async getSubscriptionPrices(): Promise<PricePlanPrices> {
      const { businessRepository } = useLegacyApi()
      return businessRepository.getSubscriptionPrices(this.id)
    },
  },
})
